import {Injector} from "@angular/core"
import {
    ConfigInfo,
    ConfiguratorParameterType,
    DataObjectReference,
    FindMaterial,
    ImageColorSpace,
    InMemoryImage,
    InMemoryImageData,
    InterfaceDescriptor,
    MaterialInfo,
    MaterialLikeNode,
    MaterialReference,
    OverlayMaterialColor,
    Parameters,
    TemplateParameterValue,
} from "@cm/template-nodes"
import {fetchOk, legacyMediaTypeSchema, MediaType, mediaTypeSchema, TypedImageData} from "@cm/utils"
import {ConfiguratorMaterialGQL} from "@common/components/viewers/configurator/configurator/configurator.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {z} from "zod"
import {CmLogger} from "@cm/log"

export type ConfiguratorUrlParameters = {
    templateId: string | undefined
    sceneId: number | undefined
    organizationUuid: string | undefined
    articleId: string | undefined
    parameters: Parameters
}

type DecodedUrlParameter = {
    id: string
    value: string
    type: ConfiguratorParameterType
}

type ParsedParameter = {
    id: string
    value: TemplateParameterValue
}

const PARAM_REGEX = /param\(([^)]*)\)/
const VALUE_REGEX = /([^(]+)\(([^)]*)\)/

const decodeUrlParameter = (key: string, value: string, logger?: CmLogger): DecodedUrlParameter | undefined => {
    const paramMatch = key.match(PARAM_REGEX)
    if (!paramMatch || !paramMatch[1]) return undefined

    const id = paramMatch[1]
    const valueMatch = value.match(VALUE_REGEX)
    if (!valueMatch || !valueMatch[1] || !valueMatch[2]) {
        logger?.error("Invalid parameter value:", value)
        return undefined
    }

    const type = valueMatch[1] as ConfiguratorParameterType

    return {id, value: valueMatch[2], type}
}

/*key has the format "param(id)", value has the format "type(value)".*/
const initializeParameterFromUrlFormat = async (key: string, val: string, injector: Injector): Promise<ParsedParameter | undefined> => {
    const decodedParameter = decodeUrlParameter(key, val)
    if (!decodedParameter) return undefined

    const {id, value, type} = decodedParameter
    const parsedValue = await initializeTemplateParameterValue(type, value, injector)

    return {id, value: parsedValue}
}

const intSchema = z.preprocess((val) => {
    if (typeof val === "string" && /^-?\d+$/.test(val)) return parseInt(val, 10)
    return val
}, z.number().int())

const numberSchema = z.preprocess(
    (val) => {
        if (typeof val === "string" && /^-?\d+(\.\d+)?$/.test(val)) return parseFloat(val)
        return val
    },
    z.number().refine((val) => !isNaN(val), {message: "Invalid number"}),
)

const booleanSchema = z.preprocess(
    (val) => (val === true || val === "true" || val === "1" ? true : val === false || val === "false" || val === "0" ? false : val),
    z.boolean(),
)

const imageDataSchema = z.object({
    data: z.instanceof(Uint8Array),
    contentType: legacyMediaTypeSchema,
})
type _ImageData = z.infer<typeof imageDataSchema>

export type ImageDataParameter = z.infer<typeof imageDataSchema>

const colorOverlaySchema = z.object({
    materialId: z.string(),
    overlay: imageDataSchema.or(z.string().url()),
    size: z.tuple([z.number(), z.number()]),
})

export type ColorOverlayParameter = z.infer<typeof colorOverlaySchema>

/*Initialize values for template inputs. Used for values defined by url parameters and by the deprecated setParameter of the configurator.
Also used to initialize url*/
export const initializeTemplateParameterValue = async (
    type: ConfiguratorParameterType,
    value: number | string | ImageDataParameter,
    injector: Injector,
): Promise<TemplateParameterValue> => {
    let parsedValue: TemplateParameterValue
    switch (type) {
        case "int":
            if (typeof value !== "string" && typeof value !== "number") throw new Error("Invalid type for int")
            parsedValue = initInt(value)
            break
        case "material":
            parsedValue = await initMaterial(value, injector)
            break
        case "material-article-id":
            parsedValue = new FindMaterial({parameters: {articleId: z.string().parse(value)}})
            break
        case "number":
        case "float":
            if (typeof value !== "string" && typeof value !== "number") throw new Error("Invalid type for number")
            parsedValue = initNumber(value)
            break
        case "boolean":
            if (typeof value !== "string" && typeof value !== "boolean") throw new Error("Invalid type for boolean")
            parsedValue = initBoolean(value)
            break
        case "image":
            parsedValue = await initImage(value)
            break
        default:
            parsedValue = z.string().parse(value)
            break
    }

    return parsedValue
}

const initMaterial = async (value: unknown, injector: Injector): Promise<MaterialLikeNode> => {
    if (typeof value === "string") return initMaterialReferenceFromId(z.string().uuid().parse(value), injector)

    const parsedColorOverlay = colorOverlaySchema.safeParse(value)
    if (parsedColorOverlay.success) {
        const {materialId, overlay, size} = parsedColorOverlay.data
        return initOverlayMaterialColor(materialId, overlay, size, injector)
    }

    throw new Error(`Material initialization not implemented for: ${value}`)
}

export async function getImageParamDataFromUrl(imageUrl: string) {
    const response = await fetch(imageUrl)
    if (!response.ok) throw new Error("Failed to fetch image from URL")

    const blob = await response.blob()

    const arrayBuffer = await blob.arrayBuffer()
    const buffer = new Uint8Array(arrayBuffer)

    const parsedMediaType = mediaTypeSchema.safeParse(blob.type)
    if (!parsedMediaType.success) throw Error(`Invalid media/content type: ${blob.type}`)

    return {
        contentType: parsedMediaType.data,
        data: buffer,
    }
}

const inMemoryImageFromEncodedImage = async (data: Uint8Array, contentType: MediaType, imageColorSpace: ImageColorSpace): Promise<InMemoryImage> => {
    const blob = new Blob([data as Uint8Array<ArrayBuffer>], {type: contentType})
    const bitmap = await createImageBitmap(blob)

    const canvas = document.createElement("canvas")
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Failed to get 2D canvas context")
    ctx.drawImage(bitmap, 0, 0)

    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)

    const colorSpace: TypedImageData["colorSpace"] = imageColorSpace === ImageColorSpace.Linear ? "linear" : "sRGB"

    return new InMemoryImage({
        parameters: {
            imageData: new InMemoryImageData({
                data: imgData.data,
                width: imgData.width,
                height: imgData.height,
                channelLayout: "RGBA",
                dataType: "uint8",
                colorSpace,
            }),
        },
    })
}

export const initOverlayMaterialColor = async (
    materialId: string,
    overlay: string | ImageDataParameter,
    size: [number, number],
    injector: Injector,
): Promise<OverlayMaterialColor> => {
    const matRef = await initMaterialReferenceFromId(materialId, injector)

    let inMemoryImage: InMemoryImage | undefined
    if (typeof overlay === "string") {
        const imageData = await getImageParamDataFromUrl(overlay)
        inMemoryImage = await inMemoryImageFromEncodedImage(imageData.data, imageData.contentType, ImageColorSpace.Srgb)
    } else {
        inMemoryImage = await inMemoryImageFromEncodedImage(overlay.data, overlay.contentType, ImageColorSpace.Srgb)
    }
    return new OverlayMaterialColor({parameters: {name: "Overlay Material Color", material: matRef, overlay: inMemoryImage, size}})
}

export const initInt = (value: string | number): number => {
    return intSchema.parse(value)
}

export const initNumber = (value: string | number): number => {
    return numberSchema.parse(value)
}
export const initBoolean = (value: string | boolean): boolean => {
    return booleanSchema.parse(value)
}

const createDataObjectReferenceFromId = (id: number): DataObjectReference => {
    return new DataObjectReference({
        parameters: {
            dataObjectId: id,
            name: "Data Object Reference",
        },
    })
}

const createInMemoryImageFromUrl = async (url: string): Promise<InMemoryImage> => {
    const response = await fetchOk(url)
    const blob = await response.blob()

    const parsedMediaType = mediaTypeSchema.safeParse(blob.type)
    if (!parsedMediaType.success) throw Error(`Invalid media/content type: ${blob.type}`)

    return inMemoryImageFromEncodedImage(new Uint8Array(await blob.arrayBuffer()), parsedMediaType.data, ImageColorSpace.Srgb)
}

const createInMemoryImageFromRawData = async (imageData: ImageDataParameter): Promise<InMemoryImage> => {
    return inMemoryImageFromEncodedImage(imageData.data, imageData.contentType, ImageColorSpace.Srgb)
}

export const initImage = async (value: number | string | ImageDataParameter): Promise<TemplateParameterValue> => {
    if (typeof value === "number") {
        // case 1: numerical legacy id number
        const parsedId = intSchema.safeParse(value)
        if (parsedId.success) {
            return createDataObjectReferenceFromId(parsedId.data)
        }

        throw new Error(`Invalid image value: ${value}`)
    }

    if (typeof value === "string") {
        // case 2: numerical legacy id string
        const parsedId = intSchema.safeParse(value)
        if (parsedId.success) {
            return createDataObjectReferenceFromId(parsedId.data)
        }

        // case 3: image url
        const imageUrl = z.string().url().safeParse(value)
        if (imageUrl.success) {
            return await createInMemoryImageFromUrl(imageUrl.data)
        }

        throw new Error(`Invalid image value: ${value}`)
    }

    // case 3: raw image data
    const parsedImageData = imageDataSchema.safeParse(value)
    if (parsedImageData.success) {
        return await createInMemoryImageFromRawData(parsedImageData.data)
    }

    throw new Error(`Invalid image value: ${value}`)
}

//The template system still operates on legacy ids, only use this function internally and do not expose the legacy ids outside of the configurator
export const createMaterialReferenceFromLegacyId = async (materialLegacyId: number, injector: Injector): Promise<MaterialReference> => {
    const configuratorMaterialGql = injector.get(ConfiguratorMaterialGQL)
    const result = await fetchThrowingErrors(configuratorMaterialGql)({materialLegacyId})
    const material = result.configuratorMaterial
    if (!material) throw new Error(`Material not found for legacy id ${materialLegacyId}`)
    const {legacyId, latestCyclesRevision} = material

    const materialRevisionLegacyId = latestCyclesRevision?.legacyId
    if (!materialRevisionLegacyId) throw new Error(`Failed to get latest revision for material ${materialLegacyId}`)

    return new MaterialReference({parameters: {name: "(Material Ref)", materialId: legacyId, materialRevisionId: materialRevisionLegacyId}})
}

//For everything outside of the configurator, use uuids
export const initMaterialReferenceFromId = async (materialId: string, injector: Injector): Promise<MaterialReference> => {
    const configuratorMaterialGql = injector.get(ConfiguratorMaterialGQL)
    const result = await fetchThrowingErrors(configuratorMaterialGql)({materialId})
    const material = result.configuratorMaterial
    if (!material) throw new Error(`Material not found for id ${materialId}`)
    const {legacyId, latestCyclesRevision} = material

    const materialRevisionLegacyId = latestCyclesRevision?.legacyId
    if (!materialRevisionLegacyId) throw new Error(`Failed to get latest revision for material ${materialId}`)

    return new MaterialReference({parameters: {name: "(Material Ref)", materialId: legacyId, materialRevisionId: materialRevisionLegacyId}})
}

export const decodeConfiguratorUrlParameters = async (parameterString: string, injector: Injector): Promise<ConfiguratorUrlParameters> => {
    const {templateId, sceneId, organizationUuid, articleId, ...restParameters} = Object.fromEntries(new URLSearchParams(parameterString))

    const parameterPromises = Object.entries(restParameters).map(([key, value]) => initializeParameterFromUrlFormat(key, value, injector))
    const parsedParameters = (await Promise.all(parameterPromises)).filter((param): param is NonNullable<typeof param> => param != null)
    const parameters = new Parameters({parameters: Object.fromEntries(parsedParameters.map((param) => [param.id, param.value]))})

    return {
        templateId: templateId,
        sceneId: sceneId ? parseInt(sceneId, 10) : undefined,
        organizationUuid,
        articleId,
        parameters,
    }
}

export const encodeConfiguratorURLParams = async (interfaceDescriptors: InterfaceDescriptor<unknown, object>[], injector: Injector): Promise<string> => {
    let result = ""
    for (const descriptor of interfaceDescriptors) {
        if (descriptor instanceof ConfigInfo && descriptor.props.type === "input" && descriptor.props.value) {
            result += `&param(${descriptor.props.id})=config(${descriptor.props.value.id})`
        }
        if (
            descriptor instanceof MaterialInfo &&
            descriptor.props.type === "input" &&
            descriptor.props.value &&
            descriptor.props.origin instanceof MaterialReference
        ) {
            const configuratorMaterialGql = injector.get(ConfiguratorMaterialGQL)
            const materialResult = await fetchThrowingErrors(configuratorMaterialGql)({
                materialLegacyId: descriptor.props.value.parameters.materialLegacyId!,
            })
            const materialId = materialResult.configuratorMaterial?.id
            if (!materialId) throw new Error("Material not found")
            result += `&param(${descriptor.props.id})=material(${materialId})`
        }
        if (
            descriptor instanceof MaterialInfo &&
            descriptor.props.type === "input" &&
            descriptor.props.value &&
            descriptor.props.origin instanceof FindMaterial
        ) {
            result += `&param(${descriptor.props.id})=material-article-id(${descriptor.props.origin.parameters.articleId})`
        }
    }
    return result
}
